import { array } from 'fp-ts';
import { pipe } from 'fp-ts/function';
import { PricingMode } from '@mews/enums';
import { subtractNumbersAsDecimals, sumNumbersAsDecimals } from '@mews/framework/numeric';
import { useAppSelector } from '../../../../utils/redux';
import { ReservationPrice } from '../../../../modules/entities/reservationPrices/types';
import { getCurrencyCode, getLanguageCode } from '../../../../modules/app/selectors';
import {
    getCurrentReservationGroupId,
    getMerchantCurrencyCodeOrEnterpriseDefaultCurrencyCode,
} from '../../../../modules/entities/reservations/selectors';
import { hasSelectedEnterpriseNetPricing } from '../../../../selectors/views/selectedEnterprise/selectedEnterprise';
import { getReservationPrices } from '../../../../modules/entities/reservationPrices/selectors';
import { localizeCurrencyValue } from '../../../../modules/globalization/selectors';
import { useGetTaxValues } from './useGetTaxValues';

const getTotalPrice = (reservationPrices: ReservationPrice[]) =>
    pipe(
        reservationPrices,
        array.map(({ totalAmount: { grossValue, netValue } }) => ({ grossValue, netValue })),
        array.reduce({ grossValue: 0, netValue: 0 }, (acc, { grossValue, netValue }) => ({
            grossValue: sumNumbersAsDecimals(acc.grossValue, grossValue),
            netValue: sumNumbersAsDecimals(acc.netValue, netValue),
        }))
    );

export const useTaxBreakDown = ({ shouldSelectedCurrencyBeUsed }: { shouldSelectedCurrencyBeUsed: boolean }) => {
    const appCurrencyCode = useAppSelector(getCurrencyCode);
    const defaultCurrencyCode = useAppSelector((state) =>
        getMerchantCurrencyCodeOrEnterpriseDefaultCurrencyCode(state, {})
    );
    const currencyCode: string = shouldSelectedCurrencyBeUsed ? appCurrencyCode : defaultCurrencyCode;
    const languageCode = useAppSelector(getLanguageCode) ?? 'en-US';
    const hasNetPricing = useAppSelector((state) => hasSelectedEnterpriseNetPricing(state, {}));
    const reservationGroupId = useAppSelector(getCurrentReservationGroupId);
    const reservationPrices = useAppSelector((state) => getReservationPrices(state, { reservationGroupId }));

    const taxValues = useGetTaxValues({ reservationPrices, currencyCode, languageCode });
    const totalPrice = getTotalPrice(reservationPrices);

    const taxTotalValue = subtractNumbersAsDecimals(totalPrice.grossValue, totalPrice.netValue);

    return useAppSelector((state) => ({
        taxTotal: localizeCurrencyValue(state, {
            currencyValue: { value: taxTotalValue, currencyCode },
            languageCode,
            pricingMode: PricingMode.Simple,
        }),
        taxValues,
        hasNetPricing,
    }));
};
